import type { ReactNode } from 'react'
import styles from './DataTable.module.css'

export type SortDirection = 'asc' | 'desc'

export interface Column<Row> {
  readonly key: string
  readonly header: string
  /** Present ⇒ the column is sortable, and this is the value sent to the API.
   *  Absent ⇒ the header renders as plain text with no control. */
  readonly sortKey?: string
  readonly numeric?: boolean
  readonly render: (row: Row) => ReactNode
}

export interface DataTableProps<Row> {
  /** Describes the table for assistive tech. A `<caption>` is the accessible
   *  name of a table; without one a screen reader announces "table" and the
   *  user has to infer the rest from the headers. */
  readonly caption: string
  readonly columns: ReadonlyArray<Column<Row>>
  readonly rows: readonly Row[]
  readonly rowKey: (row: Row) => string
  readonly sort?: { readonly by: string; readonly dir: SortDirection }
  readonly onSort?: (sortKey: string) => void
  readonly onRowClick?: (row: Row) => void
  readonly selectedKey?: string | undefined
  /** Dims the rows while a refetch is in flight. */
  readonly stale?: boolean
  readonly sortLabel: (column: string, next: SortDirection) => string
}

const ARROW: Record<SortDirection, string> = { asc: '▲', desc: '▼' }

/**
 * A sortable table with the accessibility work done once, here, rather than
 * per screen.
 *
 *  - `aria-sort` on the sorted `<th>`. This is what tells a screen-reader user
 *    the table is sorted at all; a coloured arrow tells them nothing.
 *  - The sort control is a real `<button>` inside the header, so it is
 *    reachable and operable by keyboard for free. A `<th onClick>` is not.
 *  - `scope="col"` associates each header with its column.
 *  - Row clicks are an ENHANCEMENT, never the only route to the data: the
 *    detail a row opens must also be reachable another way, because a
 *    `<tr onClick>` cannot be focused or activated from a keyboard.
 */
export function DataTable<Row>({
  caption, columns, rows, rowKey, sort, onSort, onRowClick, selectedKey, stale = false, sortLabel,
}: DataTableProps<Row>) {
  return (
    <div className={styles.wrap}>
      <table className={styles.table}>
        <caption className={styles.caption}>{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => {
              const active = sort && column.sortKey === sort.by
              const nextDir: SortDirection = active && sort.dir === 'desc' ? 'asc' : 'desc'
              return (
                <th
                  key={column.key}
                  scope="col"
                  className={[styles.th, column.numeric ? styles.numeric : ''].filter(Boolean).join(' ')}
                  aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {column.sortKey && onSort ? (
                    <button
                      type="button"
                      className={styles.sortButton}
                      onClick={() => onSort(column.sortKey ?? '')}
                      aria-label={sortLabel(column.header, nextDir)}
                    >
                      {column.header}
                      <span
                        className={[styles.indicator, active ? styles.indicatorActive : ''].filter(Boolean).join(' ')}
                        aria-hidden="true"
                      >
                        {active ? ARROW[sort.dir] : '↕'}
                      </span>
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody className={stale ? styles.stale : undefined}>
          {rows.map((row) => {
            const key = rowKey(row)
            return (
              <tr
                key={key}
                className={[
                  styles.row,
                  onRowClick ? styles.rowClickable : '',
                  selectedKey === key ? styles.rowSelected : '',
                ].filter(Boolean).join(' ')}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={[styles.td, column.numeric ? styles.numeric : ''].filter(Boolean).join(' ')}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
